import { Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Divider } from '@mui/material'
import AppDialogTitle from '../../../../ui/appDialogTitle'
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import FrameSelectDialog from '../../../../admin/inventory/frames/frameSelectDialog';
import { ValidDataService } from '../../../../../service/validDataService';
import { toast } from 'react-toastify';
import { Colors } from '../../../../../styles/theme';
import LensConfigurationDialog from './LensConfigurationDialog';
import { formatAmountForDisplay, sumMoney } from '../../../../../utils';
import { transformCatalogFrameToFrameData } from '../../../../../utils/frameDataMapper';
import { ButtonType } from './invoiceButtonComponents';
import SelectRxDialog from '../../rx/selectRxDialog';
import CreateRxInlineDialog from '../../rx/createRxInline';
import FrameSection from './FrameSection';
import RxSection from './RxSection';
import { v4 as uuidv4 } from 'uuid'
import FrameSectionPatientOwn from './FrameSectionPatientOwn';
import TreatmentSelectDialog from '../../../../admin/ValidData/validTreatment/treatmentSelectDialog';
import { FRAME_HANDLING_PATIENT_OWN, FRAME_HANDLING_STOCK } from '../invoice';

function NewGlassesDialog({ onClose, open, onSelect, customer, existingGlassesData }) {
  const [inProgress, setInProgress] = useState(false);

  // const [customerSuppliedFrame, setCustomerSuppliedFrame] = useState({
  //   invoiceItemId: null,
  //   modelNumber: '',
  //   supplier: '',
  //   brand: '',
  //   eyeSize: 0,
  //   bridgeSize: 0,
  //   templeLength: 0,
  //   aMeasurement: 0,
  //   bMeasurement: 0,
  //   dblMeasurement: 0,
  //   edMeasurement: 0,
  //   circumference: 0,
  //   edAngle: 0,
  //   frameColor: '',
  //   frameColorCode: '',
  //   material: '',
  //   shape: '',
  //   rimType: ''
  // })

  const [frameData, setFrameData] = useState({
    id: null,
    itemId: null, // Reference to inventory item (what we actually sell)
    brand: { name: '' },
    supplier: { supplierName: '' },
    properties: {
      eyeSize: 0,
      bridgeSize: 0,
      templeLength: 0,
      aMeasurement: 0,
      bMeasurement: 0,
      dblMeasurement: 0,
      edMeasurement: 0,
      circumference: 0,
      edAngle: 0,
      frameColor: '',
      frameColorCode: '',
      material: '',
      shape: '',
      rimType: ''
    },
    listPrice: 0.0,
    isTaxable: false,
    defaultTaxPercentage: 0,
    modelNumber: '',
    location: {
      name: ''
    },
    frameHandling: FRAME_HANDLING_STOCK,
    customerSuppliedFrame: {
      invoiceItemId: null,
      modelNumber: '',
      supplier: '',
      brand: '',
      eyeSize: 0,
      bridgeSize: 0,
      templeLength: 0,
      aMeasurement: 0,
      bMeasurement: 0,
      dblMeasurement: 0,
      edMeasurement: 0,
      circumference: 0,
      edAngle: 0,
      frameColor: '',
      frameColorCode: '',
      material: '',
      shape: '',
      rimType: ''
    }
  });

  const [lensData, setLensData] = useState({
    OD: null, // Will hold OD lens configuration
    OS: null, // Will hold OS lens configuration
  });

  const [frameSelectDialogOpen, setFrameSelectDialogOpen] = useState(false);
  const [lensSelectDialogOpen, setLensSelectDialogOpen] = useState(false);
  const [rxSelectDialogOpen, setRxSelectDialogOpen] = useState(false);
  const [createRxDialogOpen, setCreateRxDialogOpen] = useState(false);
  const [treatmentSelectDialogOpen, setTreatmentSelectDialogOpen] = useState(false);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [selectedRx, setSelectedRx] = useState(null);
  const [discount, setDiscount] = useState(0.00);

  const isEditing = Boolean(existingGlassesData);
  const dialogTitle = isEditing ? 'Update Glasses Order' : 'New Glasses Order';
  const primaryButtonLabel = isEditing ? 'Update Invoice' : 'Add to Invoice';

  // reset frame data on open OR populate from existing data
  useEffect(() => {
    if (open) {
      // If we're editing existing glasses data, populate the form
      if (existingGlassesData) {
        const { frame, odLens, osLens, rx } = existingGlassesData;

        // Populate frame data
        if (frame) {
          const isPatientOwnFrame = frame.frameHandling === FRAME_HANDLING_PATIENT_OWN;
          
          if (isPatientOwnFrame) {
            // Patient-owned frame
            setFrameData({
              id: null,
              itemId: null, // No inventory item for patient own frames
              supplier: { supplierName: '' },
              brand: { name: '' },
              properties: {
                eyeSize: 0, bridgeSize: 0, templeLength: 0,
                aMeasurement: 0, bMeasurement: 0, dblMeasurement: 0,
                edMeasurement: 0, circumference: 0, edAngle: 0,
                frameColor: '', frameColorCode: '', material: '', shape: '', rimType: ''
              },
              listPrice: 0.0,
              modelNumber: '',
              location: { name: '' },
              frameHandling: FRAME_HANDLING_PATIENT_OWN,
              customerSuppliedFrame: {
                invoiceItemId: frame.id,
                modelNumber: frame.customerSuppliedFrame.modelNumber || '',
                supplier: frame.customerSuppliedFrame.supplier || '',
                brand: frame.customerSuppliedFrame.brand || '',
                eyeSize: frame.customerSuppliedFrame.eyeSize || 0,
                bridgeSize: frame.customerSuppliedFrame.bridgeSize || 0,
                templeLength: frame.customerSuppliedFrame.templeLength || 0,
                aMeasurement: frame.customerSuppliedFrame.aMeasurement || 0,
                bMeasurement: frame.customerSuppliedFrame.bMeasurement || 0,
                dblMeasurement: frame.customerSuppliedFrame.dblMeasurement || 0,
                edMeasurement: frame.customerSuppliedFrame.edMeasurement || 0,
                circumference: frame.customerSuppliedFrame.circumference || 0,
                edAngle: frame.customerSuppliedFrame.edAngle || 0,
                frameColor: frame.customerSuppliedFrame.frameColor || '',
                frameColorCode: frame.customerSuppliedFrame.frameColorCode || '',
                material: frame.customerSuppliedFrame.material || '',
                shape: frame.customerSuppliedFrame.shape || '',
                rimType: frame.customerSuppliedFrame.rimType || ''
              }
            });
          } else {
            // Stock frame - backend now sends flat normalized structure in frame.frame
            const normalizedFrame = frame;
            
            if (normalizedFrame) {
              // Backend-normalized frame (flat structure)
              setFrameData({
                id: frame.id,
                itemId: normalizedFrame.itemId || frame.itemId,
                supplier: normalizedFrame.supplier || { supplierName: '' },
                brand: normalizedFrame.brand || { name: '' },
                properties: normalizedFrame.properties || {},
                listPrice: parseFloat(frame.price) || parseFloat(normalizedFrame.listPrice) || 0.0,
                modelNumber: normalizedFrame.modelNumber || '',
                location: normalizedFrame.location || { name: '' },
                frameHandling: FRAME_HANDLING_STOCK,
                customerSuppliedFrame: {
                  invoiceItemId: null,
                  modelNumber: '',
                  supplier: '',
                  brand: '',
                  eyeSize: 0,
                  bridgeSize: 0,
                  templeLength: 0,
                  aMeasurement: 0,
                  bMeasurement: 0,
                  dblMeasurement: 0,
                  edMeasurement: 0,
                  circumference: 0,
                  edAngle: 0,
                  frameColor: '',
                  frameColorCode: '',
                  material: '',
                  shape: '',
                  rimType: ''
                }
              });
            } else {
              // Fallback for old data format
              setFrameData({
                id: frame.id,
                itemId: frame.itemId || null,
                supplier: { supplierName: '' },
                brand: { name: '' },
                properties: {},
                listPrice: parseFloat(frame.price) || 0.0,
                modelNumber: frame.modelNumber || frame.description || '',
                location: frame.location || { name: '' },
                frameHandling: FRAME_HANDLING_STOCK,
                customerSuppliedFrame: {
                  invoiceItemId: null,
                  modelNumber: '',
                  supplier: '',
                  brand: '',
                  eyeSize: 0,
                  bridgeSize: 0,
                  templeLength: 0,
                  aMeasurement: 0,
                  bMeasurement: 0,
                  dblMeasurement: 0,
                  edMeasurement: 0,
                  circumference: 0,
                  edAngle: 0,
                  frameColor: '',
                  frameColorCode: '',
                  material: '',
                  shape: '',
                  rimType: ''
                }
              });
            }
          }
        }

        // Populate lens data for OD and OS
        const reconstructedLensData = {
          OD: null,
          OS: null
        };

        if (odLens?.itemConfig) {
          reconstructedLensData.OD = {
            lensModelId: odLens.itemConfig.lensModelId,
            manufacturerId: odLens.itemConfig.manufacturerId,
            lensMaterialId: odLens.itemConfig.lensMaterialId,
            lensIndexId: odLens.itemConfig.lensIndexId,
            treatmentIds: odLens.itemConfig.treatmentIds || [],
            // Expanded objects for display (populated by backend)
            model: odLens.itemConfig.model || null,
            manufacturer: odLens.itemConfig.manufacturer || null,
            material: odLens.itemConfig.material || null,
            index: odLens.itemConfig.index || null,
            treatments: odLens.itemConfig.treatments || [],
            labInstructions: odLens.labInstructions || '',
            pricing: {
              totalRetail: odLens.price || 0
            }
          };
        }

        if (osLens?.itemConfig) {
          reconstructedLensData.OS = {
            lensModelId: osLens.itemConfig.lensModelId,
            manufacturerId: osLens.itemConfig.manufacturerId,
            lensMaterialId: osLens.itemConfig.lensMaterialId,
            lensIndexId: osLens.itemConfig.lensIndexId,
            treatmentIds: osLens.itemConfig.treatmentIds || [],
            // Expanded objects for display (populated by backend)
            model: osLens.itemConfig.model || null,
            manufacturer: osLens.itemConfig.manufacturer || null,
            material: osLens.itemConfig.material || null,
            index: osLens.itemConfig.index || null,
            treatments: osLens.itemConfig.treatments || [],
            labInstructions: osLens.labInstructions || '',
            pricing: {
              totalRetail: osLens.price || 0
            }
          };
        }

        setLensData(reconstructedLensData);

        // Populate Rx
        if (rx) {
          setSelectedRx(rx);
        }

        // Warn if lens configuration data is missing (old invoices created before lensConfig was added)
        if ((odLens || osLens) && !odLens?.itemConfig && !osLens?.itemConfig) {
          toast.warning('This invoice was created before lens configuration tracking was added. Lens details cannot be edited.');
        }

        // Note: lensModelNumber and discount are not stored in lensConfig, 
        // so they will remain at defaults when editing
      } else {
        // Reset to defaults for new glasses order
        setFrameData({
          id: null,
          itemId: null,
          supplier: { supplierName: '' },
          brand: { name: '' },
          properties: {
            eyeSize: 0, bridgeSize: 0, templeLength: 0,
            aMeasurement: 0, bMeasurement: 0, dblMeasurement: 0,
            edMeasurement: 0, circumference: 0, edAngle: 0,
            frameColor: '', frameColorCode: '', material: '', shape: '', rimType: ''
          },
          listPrice: 0.0,
          modelNumber: '',
          location: { name: '' },
          frameHandling: FRAME_HANDLING_STOCK,
          customerSuppliedFrame: {
            invoiceItemId: null,
            modelNumber: '',
            supplier: '',
            brand: '',
            eyeSize: 0,
            bridgeSize: 0,
            templeLength: 0,
            aMeasurement: 0,
            bMeasurement: 0,
            dblMeasurement: 0,
            edMeasurement: 0,
            circumference: 0,
            edAngle: 0,
            frameColor: '',
            frameColorCode: '',
            material: '',
            shape: '',
            rimType: ''
          }
        });
        setLensData({
          OD: null,
          OS: null,
        });
        setSelectedRx(null);
        setSelectedTreatments([]);
      }
    }
  }, [open, existingGlassesData]);


  const handleFrameHandlingChange = (newValue) => {
    setFrameData(prevData => ({
      ...prevData,
      id: newValue === FRAME_HANDLING_PATIENT_OWN ? null : prevData.id,
      listPrice:  newValue === FRAME_HANDLING_PATIENT_OWN ? 0.0 : prevData.listPrice,
      frameHandling: newValue
    }))
  };

  // customerSuppliedFrame.modelNumber
  const handleFrameDataChange = (fieldPath, value) => {
    setFrameData(prev => {
      const newData = { ...prev };
      // keys[0] = customerSuppliedFrame
      // keys[1] = modelNumber
      const keys = fieldPath.split('.');
      let current = newData;

      // Navigate to the parent of the target field
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      // Set the final value
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };
  // --- End Logic for 'Patient Own' ---

  const handleFrameSelect = (selectedFrame) => {
    // Transform catalog data to legacy structure (centralized logic)
    const transformedFrame = transformCatalogFrameToFrameData(selectedFrame);
    
    setFrameData({ 
      ...transformedFrame, 
      frameHandling: FRAME_HANDLING_STOCK, 
      customerSuppliedFrame: { ...frameData.customerSuppliedFrame } 
    });
    
    setFrameSelectDialogOpen(false);
  };

  const handleLensSelect = (lensConfigurations) => {
    // lensConfigurations = { OD: {...config, pricing, model, material, index, treatments, rx}, OS: {...} }
    
    // Store both OD and OS configurations
    setLensData({
      OD: lensConfigurations.OD,
      OS: lensConfigurations.OS,
    });
    
    setLensSelectDialogOpen(false);
  }

  const totals = useMemo(() => {
    const treatmentTotal = selectedTreatments.reduce((sum, treatment) => sum + (parseFloat(treatment.listPrice) || 0), 0);
    const odTotal = lensData.OD?.pricing?.totalRetail || 0;
    const osTotal = lensData.OS?.pricing?.totalRetail || 0;
    return sumMoney([frameData.listPrice || 0, odTotal, osTotal, treatmentTotal]);
  }, [frameData, lensData, selectedTreatments])

  const handleTreatmentSelect = (treatment) => {
    setSelectedTreatments(prev => [...prev, treatment]);
    setTreatmentSelectDialogOpen(false);
  };

  const getByModelNo = async (type, modelNumber) => {

    if (inProgress) return; // Prevent multiple clicks

    if (!modelNumber) {
      toast.error('Please enter a model number');
      return;
    }

    setInProgress(true);
    // This function would typically make an API call to fetch frame details by model number
    const result = await ValidDataService.getInventoryItemByField('modelNumber', modelNumber, type)
    setInProgress(false);
    const { code, data } = result;
    if (code === 0 && data) {
      if (type === 'frame') {
        setFrameData({...data, frameHandling: FRAME_HANDLING_STOCK, customerSuppliedFrame: {...frameData.customerSuppliedFrame }})
      } else if (type === 'lens') {
        setLensData({...data, rx: selectedRx})
      }
    } else {
      toast.success(`${type} not found`);
    }
  }
  
  const handleSave = () => {
    // Preserve existing groupId when editing so downstream update logic can find the items
    const groupId = existingGlassesData?.groupId || uuidv4();
    const dataToSave = {
      name: 'Glasses',
      type: ButtonType.NEW_GLASSES,
      frame: { ...frameData },
      lens: { ...lensData },
      rx: selectedRx,
      //treatments: selectedTreatments,
      total: totals,
      groupId: groupId,
    }

    onSelect(dataToSave)
  }

  return (
    <Dialog onClose={onClose} open={open} fullScreen maxWidth="lg" fullWidth sx={{ bgcolor: '#f0f0f0' }} >
  <AppDialogTitle onClose={onClose} title={dialogTitle} />
      <DialogContent>
        <Grid container mt={1} spacing={1}>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel id='frame-handling-label'>Frame Handling</FormLabel>
              <RadioGroup
                aria-labelledby='frame-handling-label'
                name='frame-handling'
                row
                value={frameData.frameHandling}
                onChange={(e) => handleFrameHandlingChange(e.target.value)}>
                <FormControlLabel value="stock" control={<Radio size="small" />} label="Stock" />
                <FormControlLabel value="patient own" control={<Radio size="small" />} label="Patient Own" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item md={12} lg={6} p={1}>
            {
              frameData.frameHandling === FRAME_HANDLING_PATIENT_OWN ?
                <FrameSectionPatientOwn
                  frameData={frameData}
                  inProgress={inProgress}
                  onFrameDataChange={handleFrameDataChange}
                />
                :
                <FrameSection
                  frameData={frameData}
                  inProgress={inProgress}
                  getFrameByModelNo={getByModelNo}
                  setFrameSelectDialogOpen={setFrameSelectDialogOpen}
                  onFrameDataChange={handleFrameDataChange}
                />
            }            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, backgroundColor: Colors.secondary }}>
              <Typography sx={{ flexGrow: 1 }}>Lens Configuration</Typography>
              {lensData.OD && (
                <Button 
                  size='small' 
                  variant='outlined' 
                  onClick={() => setLensSelectDialogOpen(true)}
                  sx={{ mr: 1 }}
                >
                  Edit
                </Button>
              )}
              <Button 
                size='small' 
                variant='contained' 
                startIcon={<Add />} 
                onClick={() => {
                  setLensData({ OD: null, OS: null }); // Clear existing data
                  setLensSelectDialogOpen(true);
                }}
                disabled={!selectedRx}
              >
                {lensData.OD ? 'Reconfigure' : 'Configure Lens'}
              </Button>
            </Box>
            
            {lensData.OD ? (
              <Box sx={{ p: 2, mb: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>OD (Right Eye)</Typography>
                <Typography variant="body2">Model: {lensData.OD.model?.modelName} {lensData.OD.model?.lensType && `(${lensData.OD.model.lensType.replace('_', ' ')})`}</Typography>
                <Typography variant="body2">Material: {lensData.OD.material?.name}</Typography>
                <Typography variant="body2">Index: {lensData.OD.index?.indexValue}</Typography>
                {lensData.OD.treatments?.length > 0 && (
                  <Typography variant="body2">Treatments: {lensData.OD.treatments.map(t => t.name).join(', ')}</Typography>
                )}
                {lensData.OD.labInstructions && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Lab Instructions: {lensData.OD.labInstructions}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                  Price: {formatAmountForDisplay(lensData.OD.pricing?.totalRetail || 0)}
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>OS (Left Eye)</Typography>
                {lensData.OS ? (
                  <>
                    <Typography variant="body2">Model: {lensData.OS.model?.modelName} {lensData.OS.model?.lensType && `(${lensData.OS.model.lensType.replace('_', ' ')})`}</Typography>
                    <Typography variant="body2">Material: {lensData.OS.material?.name}</Typography>
                    <Typography variant="body2">Index: {lensData.OS.index?.indexValue}</Typography>
                    {lensData.OS.treatments?.length > 0 && (
                      <Typography variant="body2">Treatments: {lensData.OS.treatments.map(t => t.name).join(', ')}</Typography>
                    )}
                    {lensData.OS.labInstructions && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                        Lab Instructions: {lensData.OS.labInstructions}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Price: {formatAmountForDisplay(lensData.OS.pricing?.totalRetail || 0)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No OS configuration
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                {selectedRx ? 'No lens configured. Click "Configure Lens" to begin.' : 'Please select an Rx first before configuring lens.'}
              </Typography>
            )}
          </Grid>
          <Grid item md={12} lg={6} p={1}>
            <RxSection
              selectedRx={selectedRx}
              setRxSelectDialogOpen={setRxSelectDialogOpen}
              setCreateRxDialogOpen={setCreateRxDialogOpen}
              discount={discount}
              setDiscount={setDiscount}
              totals={totals}
              formatAmountForDisplay={formatAmountForDisplay}
            />
            
            {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, backgroundColor: Colors.secondary }}>
              <Typography sx={{ flexGrow: 1 }}>Lab</Typography>
              <Button size='small' variant='contained' startIcon={<Search />} onClick={() => setLabSelectDialogOpen(true)}>Select Lab</Button>
            </Box>
            {selectedLab ?
              <Typography variant="body1" sx={{ mb: 1 }}>Selected Lab: {selectedLab.name}</Typography>  :           
              <Typography variant="body1" sx={{ mb: 1 }}>Selected Lab: {'No lab selected'}</Typography>             
            }
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <SimpleDatePicker
                  label="Order Date"
                  value={orderDate}
                  onChange={(date) => setOrderDate(date)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <SimpleDatePicker
                  label="Promise Date"
                  value={promiseDate}
                  onChange={(date) => setPromiseDate(date)}
                  size="small"
                  fullWidth
                />                
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rushOrder}
                      onChange={(e) => setRushOrder(e.target.checked)}
                    />
                  }
                  label="Rush Order"
                />
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ mb: 1 }}>Lab Instructions</Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              value={labNotes}
              onChange={(e) => setLabNotes(e.target.value)}
              sx={{ mb: 2 }}
            /> */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              {/* <Typography variant="body2">Discount</Typography>
              <TextField
                variant="outlined"
                value={`$${discount.toFixed(2)}`}
                onChange={(e) => setDiscount(parseFloat(e.target.value.replace('$', '')) || 0)}
                sx={{ width: '80px', bgcolor: '#e0e0e0' }}
                disabled
              /> */}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total Price</Typography>
              <TextField
                variant="outlined"
                value={formatAmountForDisplay(totals)}
                sx={{ width: '100px', bgcolor: 'black', '& .MuiInputBase-input': { color: 'white' } }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Footer */}
      </DialogContent>
      <DialogActions sx={{ p: 1, boxShadow: 8 }}>
        <Button onClick={handleSave} variant='contained'>
          {primaryButtonLabel}
        </Button>
        <Button color="cancel" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
      <FrameSelectDialog
        open={frameSelectDialogOpen}
        onClose={() => setFrameSelectDialogOpen(false)}
        onSelect={handleFrameSelect}
      />
      <LensConfigurationDialog
        open={lensSelectDialogOpen}
        onClose={() => setLensSelectDialogOpen(false)}
        onSelect={handleLensSelect}
        selectedRx={selectedRx}
        existingLensData={lensData}
      />
      <SelectRxDialog
        open={rxSelectDialogOpen}
        onClose={() => setRxSelectDialogOpen(false)}
        onSelect={setSelectedRx}
        customerId={customer?.customer?.id}
      />

      <CreateRxInlineDialog
        open={createRxDialogOpen}
        onClose={() => setCreateRxDialogOpen(false)}
        onRxCreated={(newRx) => {
          setSelectedRx(newRx);
          setCreateRxDialogOpen(false);
        }}
        customerId={customer?.customer?.id}
      />

      <TreatmentSelectDialog
        open={treatmentSelectDialogOpen}
        onClose={() => setTreatmentSelectDialogOpen(false)}
        onSelect={handleTreatmentSelect}
      />
    </Dialog>
  )
}

export default NewGlassesDialog